import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePlatformScope } from "@/context/PlatformScope";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LogOut, Shield, KeyRound, Palette, Check, RefreshCw, Bell } from "lucide-react";
import { THEMES, applyTheme, getStoredThemeKey } from "@/lib/themes";
import { GlobalSearch } from "@/components/layout/GlobalSearch";
import { PlatformScopeSelector } from "@/components/layout/PlatformScopeSelector";
import DemoResetDialog from "@/components/admin/DemoResetDialog";

export function Topbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTheme, setActiveTheme] = useState(getStoredThemeKey());
  const [demoResetOpen, setDemoResetOpen] = useState(false);
  const isSuper = (user?.roles || []).includes("super_admin");
  const { isBound } = usePlatformScope();
  const showScope = isSuper || isBound;

  const initials = (user?.name || user?.email || "?")
    .split(/\s+|@/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header
      className="h-16 sticky top-3 z-20 bg-card rounded-2xl shadow-md flex items-center px-5 gap-3"
      data-testid="app-topbar"
    >
      <GlobalSearch />

      {showScope && (
        <>
          <div className="hidden md:block h-6 w-px bg-border/70 mx-1" />
          <PlatformScopeSelector />
        </>
      )}

      <div className="ml-auto flex items-center gap-1.5">
        {/* Notifications (visual — pulls red dot if count > 0; wires to nothing for now) */}
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 rounded-full hover:bg-muted"
          data-testid="topbar-notifications"
          title="Notifications"
        >
          <Bell className="h-4 w-4" />
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-card" />
        </Button>

        {/* Theme switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full hover:bg-muted" data-testid="theme-switcher-btn" title="Theme">
              <Palette className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 max-h-[420px] overflow-y-auto" data-testid="theme-switcher-menu">
            <DropdownMenuLabel>Theme</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {THEMES.map((t) => (
              <DropdownMenuItem
                key={t.key}
                onClick={() => setActiveTheme(applyTheme(t.key))}
                className="gap-2"
                data-testid={`theme-option-${t.key}`}
              >
                <div className="flex items-center gap-0.5">
                  <span className="h-4 w-3.5 rounded-l border border-border" style={{ backgroundColor: t.swatch.side }} />
                  <span className="h-4 w-3.5 rounded-r border border-l-0 border-border" style={{ backgroundColor: t.swatch.main }} />
                </div>
                <span className="flex-1 text-sm">{t.label}</span>
                {activeTheme === t.key && <Check className="h-3.5 w-3.5 text-primary" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 pl-1.5 pr-2 h-10 rounded-full hover:bg-muted" data-testid="topbar-user-menu">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-[11px] font-semibold bg-primary text-primary-foreground">
                  {initials || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left leading-tight">
                <div className="text-[13px] font-medium max-w-[140px] truncate">{user?.name}</div>
                <div className="text-[10px] text-muted-foreground max-w-[140px] truncate uppercase tracking-wider">
                  {user?.roles?.[0] || "user"}
                </div>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
            <div className="px-2 pb-2 text-xs text-muted-foreground truncate">{user?.email}</div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/mfa")} data-testid="menu-mfa">
              <Shield className="h-4 w-4 mr-2" /> Two-factor (TOTP)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/change-password")} data-testid="menu-change-password">
              <KeyRound className="h-4 w-4 mr-2" /> Change password
            </DropdownMenuItem>
            {isSuper && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDemoResetOpen(true)}
                  data-testid="menu-demo-reset"
                  className="text-amber-700 focus:text-amber-700"
                >
                  <RefreshCw className="h-4 w-4 mr-2" /> Reset demo data
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-destructive focus:text-destructive">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {demoResetOpen && <DemoResetDialog onClose={() => setDemoResetOpen(false)} />}
    </header>
  );
}
